import React from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import './App.css';
import ATSChecker from './components/ATS/ATSChecker';
import AuthCallback from './components/Auth/AuthCallback';
import GoogleAuth from './components/Auth/GoogleAuth';
import ErrorScreen from './components/Common/ErrorScreen';
import Dashboard from './components/Dashboard/Dashboard';
import { ResumeEditor } from './components/Editor';
import EducationForm from './components/Forms/EducationForm';
import EmploymentForm from './components/Forms/EmploymentForm';
import PersonalDetailsForm from './components/Forms/PersonalDetailsForm';
import JobDescriptionAnalyzer from './components/JobAnalyzer/JobDescriptionAnalyzer';
import LandingPage from './components/Landing/LandingPage';
import Navbar from './components/Navbar/Navbar';
import Profile from './components/Profile/Profile';
import ResumeView from './components/Resume/ResumeView';
import ReviewData from './components/Review/ReviewData';
import ConversationalFlow from './components/Stages/ConversationalFlow';
import TemplateSelection from './components/Templates/TemplateSelection';
import UploadFlow from './components/Upload/UploadFlow';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { JourneyProvider } from './contexts/JourneyContext';

// Protected Route wrapper
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <div className="loading-screen">Loading...</div>;
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/" replace />;
};

// New Journey initializer
const NewJourney: React.FC = () => {
  const { createNewJourney } = require('./contexts/JourneyContext').useJourney();
  const navigate = require('react-router-dom').useNavigate();
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    const init = async () => {
      try {
        const journey = await createNewJourney();
        console.log('Journey created:', journey);
        // Small delay to ensure context state is updated
        await new Promise(resolve => setTimeout(resolve, 100));
        navigate('/journey/forms/personal');
      } catch (error: any) {
        // Check if it's a credits error
        if (error.message && (error.message.includes('no credits') || error.message.includes('credits remaining'))) {
          setError('limit');
        } else {
          setError('general');
        }
      }
    };
    init();
    // We intentionally run this once on mount to create a fresh journey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error === 'limit') {
    return (
      <ErrorScreen
        title="No Credits Remaining"
        message="You have used all your resume credits. Purchase more credits to continue building professional, ATS-friendly resumes. You can still view and edit your existing resumes."
        actionText="Back to Dashboard"
      />
    );
  }

  if (error === 'general') {
    return (
      <ErrorScreen
        title="Something Went Wrong"
        message="We couldn't create your resume journey. Please try again."
        actionText="Back to Dashboard"
      />
    );
  }

  return <div className="loading-screen">Creating your journey...</div>;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <JourneyProvider>
          <Navbar />
          <Analytics />
          <SpeedInsights />
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/auth/google" element={<GoogleAuth />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/upload"
              element={
                <ProtectedRoute>
                  <UploadFlow />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/new-journey"
              element={
                <ProtectedRoute>
                  <NewJourney />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/journey/forms/personal"
              element={
                <ProtectedRoute>
                  <PersonalDetailsForm />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/journey/forms/education"
              element={
                <ProtectedRoute>
                  <EducationForm />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/journey/forms/employment"
              element={
                <ProtectedRoute>
                  <EmploymentForm />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/journey/stages"
              element={
                <ProtectedRoute>
                  <ConversationalFlow />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/journey/review"
              element={
                <ProtectedRoute>
                  <ReviewData />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/journey/templates"
              element={
                <ProtectedRoute>
                  <TemplateSelection />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/resume/:id"
              element={
                <ProtectedRoute>
                  <ResumeView />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/resume/:resumeId/edit"
              element={
                <ProtectedRoute>
                  <ResumeEditor />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/ats-checker/:resumeId"
              element={
                <ProtectedRoute>
                  <ATSChecker />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/job-analyzer/:resumeId"
              element={
                <ProtectedRoute>
                  <JobDescriptionAnalyzer />
                </ProtectedRoute>
              }
            />

            {/* Catch all - redirect to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </JourneyProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
